<template>
  <CustomModal
    title="Forward Message"
    v-model:visible="showModal"
    width="480px"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">To</span>
        <a-select
          v-model:value="selectedChatIds"
          mode="multiple"
          placeholder="Search conversations..."
          class="forward-select"
          :options="chatOptions"
          show-search
          allow-clear
          :filter-option="false"
          :maxTagCount="3"
          :maxTagPlaceholder="(omittedValues) => `+${omittedValues.length} more`"
          @search="handleSearch"
        />
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Message (optional)</span>
        <div class="flex flex-col rounded-lg bg_4 border_10">
          <div
            v-if="attachments?.length > 0"
            class="overflow-y-auto"
            style="max-height: 150px"
          >
            <AttachmentRenderer
              source="forward_input"
              sectionCSS="pt-2 px-2"
              background-color="var(--background-color)"
              :attachments="attachments"
              :type="attachmentRendererType.UPLOAD"
              @remove-attachment="removeAttachment"
            />
          </div>

          <div class="flex flex-col relative">
            <a-textarea
              ref="textareaRef"
              class="!resize-none !border-none !bg-transparent !shadow-none mt-[6px]"
              v-model:value="additionalMessage"
              placeholder="Add a message..."
              :auto-size="{ minRows: 3, maxRows: 6 }"
              @input="checkForMention"
              @keydown="handleKeyDown"
            />

            <div
              v-if="showMentionDropdown"
              class="mention-dropdown absolute bottom-full left-0 mb-1 w-64 bg_4 border_10 rounded-lg shadow-lg overflow-hidden z-50"
            >
              <div class="p-2 border-b border_10">
                <input
                  ref="mentionSearchRef"
                  v-model="mentionSearch"
                  type="text"
                  placeholder="Search users..."
                  class="w-full px-2 py-1 text-sm bg-transparent border_10 rounded primary_color"
                  @keydown.down.prevent="navigateMentions(1)"
                  @keydown.up.prevent="navigateMentions(-1)"
                  @keydown.enter.prevent="selectMention"
                  @keydown.esc="closeMentionDropdown"
                />
              </div>
              <div class="max-h-48 overflow-y-auto">
                <div
                  v-for="(item, index) in mentionItems"
                  :key="item.id"
                  :class="[
                    'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                    selectedMentionIndex === index ? 'bg_8' : 'hover:bg_6'
                  ]"
                  @mouseenter="selectedMentionIndex = index"
                  @click="selectMention"
                >
                  <template v-if="item.isGroupMention">
                    <div class="flex items-center justify-center rounded-full bg-[var(--white-opacity-14)]" style="width: 24px; height: 24px;">
                      <span class="text-xs primary_color font-semibold">@</span>
                    </div>
                    <span class="text-sm primary_color font-medium">@{{ item.name }}</span>
                  </template>
                  <template v-else>
                    <ProfileIcon :data="item" :size="24" :font="10" />
                    <span class="text-sm primary_color">{{ renderEntity(item) }}</span>
                  </template>
                </div>
                <div v-if="mentionItems.length === 0" class="px-3 py-2 text-sm secondary_color text-center">
                  No users found
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-row items-center gap-[6px] p-2">
            <AttachmentButton
              source="forward_input"
              label=""
              :multiple="true"
              @file-selected="handleFileSelect"
              :visible="true"
            />

            <a-tooltip placement="top" v-model:open="showEmojiPicker" trigger="click">
              <template v-slot:title>
                <EmojiPicker :native="true" @select="insertEmoji" />
              </template>

              <ThemeImage
                :icon="imageNames.EMOJI_GREY"
                class="size-5 cursor-pointer"
                alt="emoji_icon"
              />
            </a-tooltip>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Forwarding</span>
        <div class="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg_8 border_8">
          <ProfileIcon
            v-if="messageSenderData"
            :data="messageSenderData"
            :size="24"
            :font="10"
            class="shrink-0"
          />
          <span class="text-sm text_color forward-preview">{{ props.messageContent }}</span>
        </div>
      </div>
      <div
        v-if="nonMemberGroups.length > 0"
        class="px-3 py-2 rounded-lg text-sm border border-red-500/30 bg-red-500/10 text-red-400"
      >
        You are not a member of <strong>{{ nonMemberGroups.join(", ") }}</strong> — message was not forwarded to {{ nonMemberGroups.length === 1 ? "this group" : "these groups" }}.
      </div>
    </div>

    <template #footer>
      <a-button
        class="primary_medium"
        :disabled="selectedChatIds.length === 0 || forwarding"
        :loading="forwarding"
        @click="handleForward"
      >
        Forward
      </a-button>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import debounce from "lodash/debounce.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { renderEntity } from "@/src/utils/utils.js";
import { useFileUpload } from "@/src/composables/useFileUpload.js";
import { uploadDownloadAPITypes } from "@/src/constants/commonConstants.js";
import { attachmentRendererType } from "@/src/constants/attachmentConstants.js";
import ChatService from "@/src/api/ChatService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmojiPicker from "vue3-emoji-picker";
import AttachmentButton from "@/src/components/attachment_components/AttachmentButton.vue";
import AttachmentRenderer from "@/src/components/attachment_components/AttachmentRenderer.vue";
import { getRecipientType } from "@/src/utils/recipientUtils.js";

const store = useStore();

const props = defineProps({
  visible: { type: Boolean, default: false },
  messageContent: { type: String, default: "" },
  messageId: { type: [String, Number], default: null },
  messageSenderId: { type: [String, Number], default: null },
  originalAttachments: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:visible", "forwarded"]);

const showModal = computed({
  get: () => props.visible,
  set: (val) => emit("update:visible", val),
});

const selectedChatIds = ref([]);
const additionalMessage = ref("");
const forwarding = ref(false);
const textareaRef = ref(null);
const showEmojiPicker = ref(false);

// Mention state
const nonMemberGroups = ref([]);
const showMentionDropdown = ref(false);
const mentionSearch = ref("");
const mentionSearchRef = ref(null);
const selectedMentionIndex = ref(0);
const mentionStartPos = ref(0);
const mentionedUserIds = ref([]);
const hasGroupMention = ref(false);

const {
  attachments,
  uploadFiles,
  removeAttachment,
  resetAttachments,
  isUploading,
} = useFileUpload({
  api: uploadDownloadAPITypes.MARKETING_UPLOADS,
});

const activeChatId = computed(() => store.getters["chats/activeChat"]?.id);
const searchResults = ref([]);
const searchQuery = ref("");
const allFetchedGroups = ref([]);
let fetchId = 0;

const fetchAllGroups = async () => {
  const currentFetchId = ++fetchId;
  try {
    const result = await store.dispatch("chats/fetchGroups");
    // Guard against stale response if modal was closed during fetch
    if (currentFetchId !== fetchId) return;
    if (result?.success) {
      allFetchedGroups.value = result.groups || [];
    }
  } catch (error) {
    console.error("Error fetching all groups for forward:", error);
  }
};

const allChats = computed(() => {
  // Use fetched groups if available, fall back to store
  const groups = allFetchedGroups.value.length > 0
    ? allFetchedGroups.value
    : (store.getters["chats/groups"] || []);
  const membershipCache = store.getters["chats/groupMembershipCache"] || {};
  return groups.filter((group) => {
    if (group.id === activeChatId.value) return false;
    const isChannel = group.type === "public" || group.type === "private";
    if (!isChannel) return true;
    const normalizedId = String(group.id).replace(/^group_/, "");
    return membershipCache[normalizedId] !== false;
  });
});

const getChatLabel = (chat) => {
  if (chat.is_placeholder) {
    const otherUser = chat.other_user || chat.user_data;
    if (otherUser) return renderEntity(otherUser);
  }
  return chat.name || "Unknown";
};

const chatOptions = computed(() => {
  if (searchQuery.value) {
    const searchResultIds = new Set(searchResults.value.map((c) => c.id));
    const storeMap = new Map();

    allChats.value.forEach((chat) => {
      if (searchResultIds.has(chat.id)) {
        storeMap.set(chat.id, chat);
      }
    });

    searchResults.value.forEach((chat) => {
      if (chat.id !== activeChatId.value && !storeMap.has(chat.id)) {
        storeMap.set(chat.id, chat);
      }
    });

    return Array.from(storeMap.values()).map((chat) => ({
      value: chat.id,
      label: getChatLabel(chat),
    }));
  }

  return allChats.value.map((chat) => ({
    value: chat.id,
    label: getChatLabel(chat),
  }));
});

const handleSearch = debounce(async (value) => {
  searchQuery.value = value;
  if (!value) {
    searchResults.value = [];
    return;
  }
  try {
    const response = await ChatService.fetchGroups({
      search: value,
      limit: 500,
      offset: 0,
    });
    if (response?.success) {
      searchResults.value = response?.conversations || response?.groups || [];
    }
  } catch (error) {
    console.error("Error searching groups:", error);
  }
}, 300);

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const messageSenderData = computed(() => {
  if (!props.messageSenderId) return null;
  return allUsers.value.find(u => Number(u.id) === Number(props.messageSenderId)) || null;
});

const groupMentionOptions = computed(() => {
  const search = mentionSearch.value.toLowerCase().trim();
  const options = [
    { id: "__here__", name: "here", isGroupMention: true },
    { id: "__everyone__", name: "everyone", isGroupMention: true },
  ];
  if (!search) return options;
  return options.filter((o) => o.name.includes(search));
});

const filteredMentionUsers = computed(() => {
  const search = mentionSearch.value.toLowerCase().trim();
  return allUsers.value.filter((user) => {
    if (user.id === currentUserId.value) return false;
    const name = renderEntity(user).toLowerCase();
    return name.includes(search);
  });
});

const mentionItems = computed(() => {
  return [...groupMentionOptions.value, ...filteredMentionUsers.value];
});

const mentionsPayload = computed(() => {
  if (hasGroupMention.value) return ["here"];
  return mentionedUserIds.value;
});

const getTextareaEl = () => {
  return textareaRef.value?.$el?.querySelector("textarea") ||
         textareaRef.value?.$refs?.textarea ||
         textareaRef.value;
};

const checkForMention = () => {
  try {
    const text = additionalMessage.value || "";
    const lastAtIndex = text.lastIndexOf("@");

    if (lastAtIndex === -1) {
      if (showMentionDropdown.value) closeMentionDropdown();
      return;
    }

    const tArea = getTextareaEl();
    const cursorPos = tArea?.selectionStart || text.length;
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtBeforeCursor = textBeforeCursor.lastIndexOf("@");

    if (lastAtBeforeCursor !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtBeforeCursor + 1);
      if (!textAfterAt.includes(" ")) {
        mentionStartPos.value = lastAtBeforeCursor;
        mentionSearch.value = textAfterAt;
        showMentionDropdown.value = true;
        selectedMentionIndex.value = 0;
        return;
      }
    }

    if (showMentionDropdown.value) closeMentionDropdown();
  } catch (error) {
    console.error("Error checking for mention:", error);
  }
};

const closeMentionDropdown = () => {
  showMentionDropdown.value = false;
  mentionSearch.value = "";
  selectedMentionIndex.value = 0;
};

const navigateMentions = (direction) => {
  const maxIndex = mentionItems.value.length - 1;
  let newIndex = selectedMentionIndex.value + direction;
  if (newIndex < 0) newIndex = maxIndex;
  if (newIndex > maxIndex) newIndex = 0;
  selectedMentionIndex.value = newIndex;
};

const selectMention = () => {
  try {
    if (mentionItems.value.length === 0) return;

    const item = mentionItems.value[selectedMentionIndex.value];
    if (!item) return;

    const tArea = getTextareaEl();
    const beforeMention = additionalMessage.value.substring(0, mentionStartPos.value);
    const cursorPos = tArea?.selectionStart || additionalMessage.value.length;
    const afterCursor = additionalMessage.value.substring(cursorPos);

    if (item.isGroupMention) {
      additionalMessage.value = `${beforeMention}@${item.name} ${afterCursor}`;
      hasGroupMention.value = true;
    } else {
      const userName = renderEntity(item);
      additionalMessage.value = `${beforeMention}@${userName} ${afterCursor}`;
      if (!mentionedUserIds.value.includes(item.id)) {
        mentionedUserIds.value.push(item.id);
      }
    }

    closeMentionDropdown();

    nextTick(() => {
      const label = item.isGroupMention ? item.name : renderEntity(item);
      const newCursorPos = beforeMention.length + label.length + 2;
      if (tArea && tArea.focus) {
        tArea.focus();
        if (tArea.setSelectionRange) {
          tArea.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    });
  } catch (error) {
    console.error("Error selecting mention:", error);
  }
};

const handleKeyDown = (e) => {
  if (showMentionDropdown.value) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      navigateMentions(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      navigateMentions(-1);
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      selectMention();
    } else if (e.key === "Escape") {
      closeMentionDropdown();
    }
  }
};

const insertEmoji = (emoji) => {
  try {
    const emojiChar = emoji.i || emoji.native || emoji;
    if (!emojiChar) return;
    additionalMessage.value = (additionalMessage.value || "") + emojiChar;
    showEmojiPicker.value = false;
  } catch (error) {
    console.error("Error inserting emoji:", error);
  }
};

const handleFileSelect = async (files) => {
  try {
    await uploadFiles(files);
  } catch (error) {
    console.error("Error uploading files:", error);
    message.error("Failed to upload files");
  }
};

const formatAttachmentsForAPI = (attachmentList) => {
  if (!attachmentList || attachmentList.length === 0) return [];
  return attachmentList.map((attachment) => {
    const payload = {
      url: attachment.url,
      media_type: attachment.content_type || "application/octet-stream",
    };
    if (attachment.file_name) {
      payload.file_name = attachment.file_name;
    }
    return payload;
  }).filter((a) => a.url);
};

const checkMembership = async (chat) => {
  const isDM = chat.type === "dm" || chat.type === "self_dm" || chat.type === "group_dm" || chat.is_dm === true;
  if (isDM) return true;

  try {
    const groupId = String(chat.id).replace(/^group_/, "");
    const response = await ChatService.fetchGroupMembers(groupId);
    if (response?.success && response?.members) {
      return response.members.some(
        (m) => Number(m.user_id || m.user?.id) === Number(currentUserId.value),
      );
    }
    return false;
  } catch {
    return false;
  }
};

const handleForward = async () => {
  if (selectedChatIds.value.length === 0) return;
  if (isUploading.value) {
    message.warning("Please wait for file upload to complete");
    return;
  }

  forwarding.value = true;
  nonMemberGroups.value = [];

  const groups = store.getters["chats/groups"] || [];
  const prepend = additionalMessage.value.trim();
  const text = prepend
    ? `${prepend}\n\n${props.messageContent}`
    : props.messageContent;

  const newAttachments = formatAttachmentsForAPI(attachments.value);
  const originalFormatted = (props.originalAttachments || [])
    .map((a) => ({
      url: a.url,
      media_type: a.media_type || a.content_type || "application/octet-stream",
      ...(a.file_name ? { file_name: a.file_name } : {}),
    }))
    .filter((a) => a.url);
  const formattedAttachments = [...originalFormatted, ...newAttachments];

  let successCount = 0;
  let lastChat = null;
  const skippedGroups = [];

  let failCount = 0;

  for (const chatId of selectedChatIds.value) {
    const chat = groups.find((g) => g.id === chatId)
      || allFetchedGroups.value.find((g) => g.id === chatId)
      || searchResults.value.find((g) => g.id === chatId);
    if (!chat) continue;

    if (!chat.is_placeholder) {
      const isMember = await checkMembership(chat);
      if (!isMember) {
        skippedGroups.push(chat.name || "Unknown");
        continue;
      }
    }

    try {
      let payload;

      if (chat.is_placeholder) {
        const otherUser = chat.other_user || chat.user_data;
        const placeholderUserId = otherUser?.id
          || (String(chat.id).startsWith("user_") ? Number(String(chat.id).replace("user_", "")) : null);

        payload = {
          recipient_type: 1,
          recipient_id: null,
          text,
          thread_id: null,
          attachments: formattedAttachments,
          mentions: [...mentionsPayload.value],
          mention_groups: [],
          is_forwarded: true,
          user_ids: [placeholderUserId],
          group_name: otherUser ? renderEntity(otherUser) : chat.name || null,
        };
      } else {
        const recipientType = getRecipientType(chat.type);
        const recipientId = Number(String(chat.id).replace(/^group_/, ""));

        payload = {
          recipient_type: recipientType,
          recipient_id: recipientId,
          text,
          thread_id: null,
          attachments: formattedAttachments,
          mentions: [...mentionsPayload.value],
          mention_groups: [],
          is_forwarded: true,
        };
      }

      const response = await ChatService.createMessage(payload);
      if (response?.success) {
        successCount++;
        if (chat.is_placeholder) {
          let newGroup = response?.group || response?.conversation;
          if (!newGroup) {
            await store.dispatch("chats/fetchGroups");
            const allGroups = store.getters["chats/groups"] || [];
            const recipientId = response?.message?.recipient_id;
            const otherUser = chat.other_user || chat.user_data;
            const normalizedName = otherUser ? renderEntity(otherUser).toLowerCase().trim() : null;
            newGroup = allGroups.find((g) => {
              if (g.is_placeholder) return false;
              if (recipientId && String(g.id) === String(recipientId)) return true;
              if (normalizedName && g.is_dm && g.name && g.name.toLowerCase().trim() === normalizedName) return true;
              return false;
            });
          }
          if (newGroup) {
            await store.dispatch("chats/addGroupToList", newGroup);
            lastChat = newGroup;
          }
        } else {
          lastChat = chat;
        }
      } else {
        failCount++;
      }
    } catch (error) {
      console.error("Error forwarding message to chat:", chat.id, error);
      failCount++;
    }
  }

  nonMemberGroups.value = skippedGroups;

  if (successCount > 0 && failCount > 0) {
    message.warning(
      `Message forwarded to ${successCount} conversation${successCount > 1 ? "s" : ""}, failed for ${failCount}`,
    );
  } else if (successCount > 0) {
    message.success(
      successCount === 1
        ? "Message forwarded"
        : `Message forwarded to ${successCount} conversations`,
    );
    if (skippedGroups.length === 0) {
      emit("forwarded", lastChat);
    }
  } else if (skippedGroups.length === 0) {
    message.error("Failed to forward message");
  }

  forwarding.value = false;
};

watch(showModal, (newVal) => {
  if (newVal) {
    fetchAllGroups();
  } else {
    selectedChatIds.value = [];
    additionalMessage.value = "";
    mentionedUserIds.value = [];
    hasGroupMention.value = false;
    nonMemberGroups.value = [];
    searchResults.value = [];
    searchQuery.value = "";
    allFetchedGroups.value = [];
    fetchId++;
    resetAttachments();
    closeMentionDropdown();
    showEmojiPicker.value = false;
  }
});

watch(additionalMessage, () => {
  setTimeout(() => checkForMention(), 10);

  if (hasGroupMention.value) {
    const text = (additionalMessage.value || "").toLowerCase();
    if (!/(^|[\s])@(here|everyone)\b/.test(text)) {
      hasGroupMention.value = false;
    }
  }
}, { flush: "post" });
</script>

<style scoped>
.forward-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-dropdown {
  background-color: var(--background-color, #1e1e1e);
  border: 1px solid var(--border-color, #404040);
  max-height: 300px;
}
</style>
